import { getFriendlyGenerationError } from '../utils/generation-errors.js';
import { classifyGenerationFailure, copyGenerationErrorDetails } from '../utils/generation-error-details.js';

/**
 * Pure LLM request policy: the overall deadline, error classification and the
 * describe-image input check. No Svelte/SDK imports so node tests can load it.
 */

function getPositiveMs(value, fallback) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

const env = import.meta.env || {};

// One budget for a whole structured call: model wait + every rung of the
// retry ladder. No new request starts once it is spent.
export const LLM_DEADLINE_MS = getPositiveMs(env.VITE_LLM_TIMEOUT_MS, 150000);

// A rung needs at least this much budget left to be worth starting.
export const MIN_ATTEMPT_MS = 5000;

// UI watchdogs must outlast the LLM deadline so the deadline, not the UI,
// decides when a request is over.
export const LLM_UI_TIMEOUT_MS = LLM_DEADLINE_MS + 15000;

export function createDeadline(budgetMs = LLM_DEADLINE_MS, now = Date.now()) {
  return now + budgetMs;
}

export function remainingMs(deadlineAt, now = Date.now()) {
  return Math.max(0, deadlineAt - now);
}

export function canStartAttempt(deadlineAt, now = Date.now()) {
  return remainingMs(deadlineAt, now) >= MIN_ATTEMPT_MS;
}

export const LLM_TIMEOUT_MESSAGE = 'The AI model took too long to respond. Please try again.';
export const LLM_OFFLINE_MESSAGE = 'The AI prompt model is offline right now. Please try again shortly.';

/**
 * Classify an LLM failure: 'auth' | 'connecting' | 'balance' | 'spark-tier' |
 * 'offline' | 'timeout' | 'other'.
 */
export function classifyLLMError(error) {
  const failure = classifyGenerationFailure(error);
  if (failure !== 'other') return failure;
  if (error?.kind) return error.kind;

  const message = String(error?.message || error || '').toLowerCase();

  if (message.includes('still connecting')) return 'connecting';
  if (message.includes('insufficient_balance')
    || (message.includes('insufficient') && message.includes('balance'))) {
    return 'balance';
  }
  if (message.includes('premium spark') || message.includes('free spark')
    || message.includes('4054')) {
    return 'spark-tier';
  }
  if (message.includes('waiting for llm models') || message.includes('model is offline')
    || message.includes('no worker available')) {
    return 'offline';
  }
  if (message.includes('timeout') || message.includes('timed out') || message.includes('took too long')) {
    return 'timeout';
  }
  return 'other';
}

/**
 * Return an Error with user-facing copy and `kind` set. Auth and connecting
 * errors already carry their own copy and pass through.
 */
export function toFriendlyLLMError(error) {
  const kind = classifyLLMError(error);
  if (kind === 'auth' || kind === 'connecting' || error?.kind) {
    if (error && typeof error === 'object' && !error.kind) error.kind = kind;
    return error;
  }

  const raw = String(error?.message || error || '');
  let message = raw;
  if (['balance', 'spark-tier', 'model-retired', 'queue', 'billing-recovery', 'plan-limit', 'trial-limit', 'fair-use', 'entitlement'].includes(kind)) {
    message = getFriendlyGenerationError(error, { isLoggedIn: true });
  } else if (kind === 'offline') {
    message = LLM_OFFLINE_MESSAGE;
  } else if (kind === 'timeout') {
    message = LLM_TIMEOUT_MESSAGE;
  }

  const friendly = copyGenerationErrorDetails(new Error(message), error);
  friendly.kind = kind;
  return friendly;
}

// The Sogni vision chat API accepts inline base64 JPEG/PNG data URIs only.
// A downscaled 1024px JPEG is typically 150-500K chars.
export const MAX_IMAGE_DATA_URL_CHARS = 1000000;
const DATA_URL_PATTERN = /^data:(image\/(?:jpeg|png));base64,([A-Za-z0-9+/]+={0,2})$/;

export function assertImageDataUrl(value) {
  if (typeof value !== 'string' || value.length === 0) {
    throw new Error('An image is required and must be a base64 data URL');
  }
  if (value.length > MAX_IMAGE_DATA_URL_CHARS) {
    throw new Error('Image is too large. Please use a smaller image');
  }
  if (!DATA_URL_PATTERN.test(value)) {
    throw new Error('Image must be a base64 data:image/jpeg or data:image/png URL');
  }
}
